import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

// ── Types ─────────────────────────────────────────────────────────────────────

export type TreatmentPlanRow = Database['public']['Tables']['tooth_treatment_plans']['Row'];

export type PlanStatus = 'proposed' | 'accepted' | 'scheduled' | 'completed' | 'cancelled';

export interface EnrichedPlanItem extends TreatmentPlanRow {
  procedure_name_da?: string | null;
  proposer_name?: string | null;
}

export interface PlanVisitGroup {
  visit_number: number;
  items: EnrichedPlanItem[];
  subtotal: number;
  status: PlanStatus;
}

// ── Query Keys ────────────────────────────────────────────────────────────────

export const treatmentPlansQueryKey = (patientId: string | null) =>
  ['dental', 'treatment-plans', patientId] as const;

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Fetch all active treatment plan items for a patient, enriched with
 * procedure code name and proposer name. Grouped by visit_number.
 */
export function usePatientTreatmentPlans(patientId: string | null) {
  return useQuery({
    queryKey: treatmentPlansQueryKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<EnrichedPlanItem[]> => {
      if (!patientId) return [];

      // treatment_type is plain text (a procedure code), not a foreign key,
      // so the name is resolved from dental_procedure_codes the same way
      // ShortcutsPanel maps code -> name_da.
      const [planRes, codesRes] = await Promise.all([
        supabase
          .from('tooth_treatment_plans')
          .select('*, proposer:users!tooth_treatment_plans_proposed_by_fkey(name)')
          .eq('patient_id', patientId)
          .neq('plan_status', 'cancelled')
          .order('visit_number', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase.from('dental_procedure_codes').select('code, name_da'),
      ]);

      if (planRes.error) throw planRes.error;
      if (codesRes.error) throw codesRes.error;

      const nameByCode = new Map((codesRes.data ?? []).map((row) => [row.code, row.name_da]));

      return (planRes.data ?? []).map((row) => {
        const { proposer, ...plan } = row;
        return {
          ...plan,
          procedure_name_da: nameByCode.get(plan.treatment_type) ?? null,
          proposer_name: proposer?.name ?? null,
        };
      });
    },
    staleTime: 15_000,
  });
}

/**
 * Derive visit groups from flat plan items.
 */
export function groupPlanItemsByVisit(items: EnrichedPlanItem[]): PlanVisitGroup[] {
  const map = new Map<number, EnrichedPlanItem[]>();

  for (const item of items) {
    const existing = map.get(item.visit_number) ?? [];
    existing.push(item);
    map.set(item.visit_number, existing);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a - b)
    .map(([visit_number, visitItems]) => {
      const subtotal = visitItems.reduce(
        (sum, it) => sum + (it.estimated_cost ?? 0),
        0,
      );
      // Aggregate status: lowest common status wins (proposed < accepted < scheduled)
      const statusOrder: PlanStatus[] = ['proposed', 'accepted', 'scheduled', 'completed', 'cancelled'];
      const statuses = visitItems.map((it) => it.plan_status as PlanStatus);
      const minStatus =
        statusOrder.find((s) => statuses.includes(s)) ?? 'proposed';
      return { visit_number, items: visitItems, subtotal, status: minStatus };
    });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useUpsertPlanItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      id?: string;
      toothFdi: string;
      treatmentType: string;
      treatmentDescription: string;
      visitNumber?: number;
      estimatedCost?: number;
      diagnosis?: string;
      surfacesTreated?: string[];
      plannedDate?: string;
      planStatus?: PlanStatus;
    }) => {
      const { data, error } = await supabase.rpc('upsert_treatment_plan', {
        p_patient_id: params.patientId,
        p_id: params.id ?? undefined,
        p_tooth_fdi: params.toothFdi,
        p_treatment_type: params.treatmentType,
        p_treatment_description: params.treatmentDescription,
        p_visit_number: params.visitNumber ?? 1,
        p_estimated_cost: params.estimatedCost ?? undefined,
        p_diagnosis: params.diagnosis ?? undefined,
        p_surfaces_treated: params.surfacesTreated ?? undefined,
        p_planned_date: params.plannedDate ?? undefined,
        p_plan_status: params.planStatus ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: treatmentPlansQueryKey(vars.patientId) });
    },
  });
}

export function useDeletePlanItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: { patientId: string; planId: string }) => {
      const { error } = await supabase.rpc('delete_treatment_plan', {
        p_id: params.planId,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: treatmentPlansQueryKey(vars.patientId) });
    },
  });
}

export function useReorderPlanVisit() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      planId: string;
      newVisitNumber: number;
    }) => {
      const { error } = await supabase.rpc('reorder_plan_visit', {
        p_plan_id: params.planId,
        p_visit_number: params.newVisitNumber,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: treatmentPlansQueryKey(vars.patientId) });
    },
  });
}

export function useConvertPlanToTreatment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      planId: string;
      clinicId: string;
      actualCost?: number;
    }) => {
      // The function takes the plan's clinic_id and sets treatment_date to
      // planned_date, or the current date when planned_date is null. It has
      // no argument for a clinic id or a treatment date. p_notes_addendum is
      // the only other input, and these buttons do not collect a note.
      if (!params.clinicId) throw new Error('No clinic ID provided');
      const { data, error } = await supabase.rpc('convert_plan_to_treatment', {
        p_plan_id: params.planId,
        p_actual_cost: params.actualCost ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: treatmentPlansQueryKey(vars.patientId) });
      qc.invalidateQueries({ queryKey: ['dental', 'treatments', vars.patientId] });
    },
  });
}

export function useUpdatePlanStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      planId: string;
      status: PlanStatus;
    }) => {
      const { error } = await supabase
        .from('tooth_treatment_plans')
        .update({ plan_status: params.status, updated_at: new Date().toISOString() })
        .eq('id', params.planId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: treatmentPlansQueryKey(vars.patientId) });
    },
  });
}
